/**
 * Календарь элементов.
 *
 * Раскладывает по срокам всё, у чего есть дата: задачи, исследования,
 * вопросы, блокеры. Перетаскивание меняет срок — той же операцией изменения
 * элемента, что и форма.
 *
 * Слева — колонка «Без срока»: перетащив оттуда, срок назначают, перетащив
 * обратно — снимают. Это единственный способ работать со сроками, не открывая
 * каждый элемент.
 */

import {
  closestCorners,
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useLanded } from '@/lib/use-landed';
import type { ItemSummaryDto } from '@project-os/contracts';
import { DISPLAY_TIMEZONE, getItemTypeDefinition } from '@project-os/contracts';
import { ApiError } from '@/api/client';
import { useUpdateItem } from '@/api/items';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DraggableItemCard, ItemCardOverlay } from './ItemCard';

const NO_DUE_DATE = '__no-date__';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

/** Ключ дня в часовом поясе отображения: календарь рисуется по нему. */
function dayKey(date: Date): string {
  const formatted = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: DISPLAY_TIMEZONE,
  }).format(date);
  return formatted;
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** Сетка месяца: недели с понедельника, с «хвостами» соседних месяцев. */
function buildGrid(month: Date): Date[] {
  const first = startOfMonth(month);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - offset);

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

export function ItemsCalendar({
  projectId,
  items,
  canEdit,
  onOpenItem,
}: {
  projectId: string;
  items: ItemSummaryDto[];
  canEdit: boolean;
  onOpenItem: (itemId: string) => void;
}) {
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [dragging, setDragging] = useState<ItemSummaryDto | null>(null);
  const update = useUpdateItem(projectId);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const grid = useMemo(() => buildGrid(month), [month]);
  const today = dayKey(new Date());
  const currentMonth = month.getMonth();

  const { byDay, undated } = useMemo(() => {
    const map = new Map<string, ItemSummaryDto[]>();
    const without: ItemSummaryDto[] = [];

    for (const item of items) {
      if (item.dueDate) {
        const list = map.get(item.dueDate);
        if (list) list.push(item);
        else map.set(item.dueDate, [item]);
      } else if (getItemTypeDefinition(item.type).supportsDueDate && !item.completedAt) {
        // Показываем только то, чему срок вообще можно назначить.
        without.push(item);
      }
    }

    return { byDay: map, undated: without };
  }, [items]);

  const { landedId, markLanded } = useLanded();

  const handleDragEnd = async (event: DragEndEvent) => {
    setDragging(null);

    const item = event.active.data.current?.item as ItemSummaryDto | undefined;
    // Карточки тоже принимают бросок, поэтому день берётся из данных цели.
    const overData = event.over?.data.current as { columnId?: string } | undefined;
    const target = overData?.columnId ?? (event.over?.id ? String(event.over.id) : null);
    if (!item || !target) return;

    const nextDate = target === NO_DUE_DATE ? null : target;
    if ((item.dueDate ?? null) === nextDate) return;

    if (!getItemTypeDefinition(item.type).supportsDueDate) {
      toast.error(`Срок не применяется к типу «${getItemTypeDefinition(item.type).labelRu}»`);
      return;
    }

    try {
      await update.mutateAsync({ itemId: item.id, dueDate: nextDate });
      markLanded(item.id);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось изменить срок');
    }
  };

  const monthLabel = new Intl.DateTimeFormat('ru-RU', {
    month: 'long',
    year: 'numeric',
  }).format(month);

  return (
    <DndContext
      collisionDetection={closestCorners}
      sensors={sensors}
      onDragStart={(event) =>
        setDragging((event.active.data.current?.item as ItemSummaryDto) ?? null)
      }
      onDragEnd={(event) => void handleDragEnd(event)}
      onDragCancel={() => setDragging(null)}
    >
      <div className="flex h-full min-h-0 gap-4 px-6 pt-1 pb-4">
        <UndatedColumn
          items={undated}
          canEdit={canEdit}
          onOpenItem={onOpenItem}
          landedId={landedId}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="mb-2 flex items-center gap-2">
            <h3 className="text-sm font-semibold first-letter:uppercase">{monthLabel}</h3>
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="icon"
              className="icon-nudge-back size-7"
              onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
              aria-label="Предыдущий месяц"
            >
              <ChevronLeftIcon className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={() => setMonth(startOfMonth(new Date()))}
            >
              Сегодня
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="icon-nudge size-7"
              onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
              aria-label="Следующий месяц"
            >
              <ChevronRightIcon className="size-4" />
            </Button>
          </div>

          <div className="mb-1 grid grid-cols-7 gap-1 text-2xs text-muted-foreground">
            {WEEKDAYS.map((weekday) => (
              <div key={weekday} className="px-1.5 py-0.5">
                {weekday}
              </div>
            ))}
          </div>

          <div className="grid min-h-0 flex-1 auto-rows-fr grid-cols-7 gap-1 overflow-y-auto">
            {grid.map((day) => {
              const key = dayKey(day);
              return (
                <CalendarDay
                  key={key}
                  dayKey={key}
                  dayNumber={day.getDate()}
                  isToday={key === today}
                  isOtherMonth={day.getMonth() !== currentMonth}
                  items={byDay.get(key) ?? []}
                  canEdit={canEdit}
                  onOpenItem={onOpenItem}
                  landedId={landedId}
                />
              );
            })}
          </div>
        </div>
      </div>

      <DragOverlay dropAnimation={null}>
        {dragging ? <ItemCardOverlay item={dragging} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

function UndatedColumn({
  items,
  canEdit,
  onOpenItem,
  landedId,
}: {
  items: ItemSummaryDto[];
  canEdit: boolean;
  onOpenItem: (itemId: string) => void;
  landedId: string | null;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: NO_DUE_DATE,
    data: { columnId: NO_DUE_DATE },
    disabled: !canEdit,
  });

  return (
    <section
      ref={setNodeRef}
      className={cn(
        'drop-zone flex w-56 shrink-0 flex-col rounded-xl',
        isOver ? 'bg-primary-soft ring-1 ring-primary/50' : 'bg-muted/40',
      )}
    >
      <header className="flex items-center gap-2 px-3 py-2.5">
        <h3 className="text-xs font-semibold">Без срока</h3>
        <span className="text-2xs text-muted-foreground tabular-nums">{items.length}</span>
      </header>

      <div className="flex-1 space-y-2 overflow-y-auto px-2 pb-2">
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item) => (
            <DraggableItemCard
              key={item.id}
              item={item}
              columnId={NO_DUE_DATE}
              onOpen={onOpenItem}
              compact
              landed={item.id === landedId}
            />
          ))}
        </SortableContext>

        {items.length === 0 ? (
          <p className="px-1 py-6 text-center text-xs text-muted-foreground/60">Всё со сроками</p>
        ) : null}
      </div>
    </section>
  );
}

function CalendarDay({
  dayKey: key,
  dayNumber,
  isToday,
  isOtherMonth,
  items,
  canEdit,
  onOpenItem,
  landedId,
}: {
  dayKey: string;
  dayNumber: number;
  isToday: boolean;
  isOtherMonth: boolean;
  items: ItemSummaryDto[];
  canEdit: boolean;
  onOpenItem: (itemId: string) => void;
  landedId: string | null;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: key,
    data: { columnId: key },
    disabled: !canEdit,
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'drop-zone flex min-h-24 flex-col rounded-lg p-1',
        isOver
          ? 'bg-primary-soft ring-1 ring-primary/50'
          : isOtherMonth
            ? 'bg-muted/20'
            : 'bg-muted/40',
      )}
    >
      <span
        className={cn(
          'mb-1 px-1 text-2xs tabular-nums',
          isOtherMonth ? 'text-muted-foreground/50' : 'text-muted-foreground',
          isToday && 'w-fit rounded bg-primary px-1.5 font-semibold text-primary-foreground',
        )}
      >
        {dayNumber}
      </span>

      <div className="flex-1 space-y-1 overflow-y-auto">
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item) => (
            <DraggableItemCard
              key={item.id}
              item={item}
              columnId={key}
              onOpen={onOpenItem}
              compact
              landed={item.id === landedId}
            />
          ))}
        </SortableContext>
      </div>
    </div>
  );
}
